import moment from 'moment';
import FieldNodeMulti from 'src/components/common/form/FieldNodeMulti';
import FieldNodeSingle from 'src/components/common/form/FieldNodeSingle';
import { FieldHookProps } from 'src/components/common/form/form.types';
import { DocumentField } from 'src/dto/response/getFieldsForDocumentTemplate';
import { DropdownMasterValues, Field } from 'src/dto/response/getTaskDetails';
import usePackagingTypes from 'src/hooks/fields/containerDetails/usePackagingTypes';
import useValidDimensionUnits from 'src/hooks/fields/containerDetails/useValidDimensionUnits';
import useValidVolumeUnit from 'src/hooks/fields/containerDetails/useValidVolumeUnits';
import useValidWeightUnit from 'src/hooks/fields/containerDetails/useValidWeightUnits';
import useAddress from 'src/hooks/fields/useAddress';
import useCarriers from 'src/hooks/fields/useCarriers';
import useCountry from 'src/hooks/fields/useCountry';
import useCourierPartner from 'src/hooks/fields/useCourierPartner';
import useEntryPort from 'src/hooks/fields/useEntryPort';
import useFF from 'src/hooks/fields/useFF';
import useMode from 'src/hooks/fields/useMode';
import usePlant from 'src/hooks/fields/usePlant';
import useSEPorts from 'src/hooks/fields/useSEPorts';
import useServices from 'src/hooks/fields/useServices';
import useCheckbox from 'src/hooks/form/useCheckbox';
import useDate from 'src/hooks/form/useDate';
import useDocument from 'src/hooks/form/useDocument';
import useFieldGroup from 'src/hooks/form/useFieldGroup';
import useInput from 'src/hooks/form/useInput';
import useInputNumber from 'src/hooks/form/useInputNumber';
import { useStaticSelect } from 'src/hooks/form/useSelect';
import { isValidArray, onlyUnique } from 'src/Library/Helper';
import { RFQ_QUOTE_FIELD_TYPE } from '../SE.constants';
import useContainerSize from './../../../../hooks/fields/containerDetails/useContainerSize';
import useContainerTypes from './../../../../hooks/fields/containerDetails/useContainerTypes';
import useShippingLines from './../../../../hooks/fields/useShippingLines';
import useUserAllocation from './hooks/UserAllocation/useUserAllocation';
import useAdditionalCharges from './hooks/useAdditionalCharges';
import useRfqBids from './hooks/useRfqBids';
import useSEQuotes from './hooks/useSEQuotes';
import useSailingSchedule from './hooks/useSailingSchedule';
import useSEGenericMultiSelect from './hooks/useSEGenericMultiSelect';
import useSEGenericSelect from './hooks/useSEGenericSelect';
import { SEFieldGroupWithHook } from './SEFieldGroup';

export const getFieldHookByType = (type: string) => {
  switch (type.toUpperCase()) {
    case 'PORTS':
      return useSEPorts;
    case 'CARRIERS':
      return useCarriers;
    case 'FF':
      return useFF;
    case 'ADDRESS':
      return useAddress;
    case 'COUNTRY':
      return useCountry;
    case 'PLANTS':
      return usePlant;
    case 'SERVICES':
      return useServices;
    case 'DOCUMENT':
      return useDocument;
    case 'DROPDOWN':
      return useStaticSelect;
    case 'DATE':
      return useDate;
    case 'NUMBER':
      return useInputNumber;
    case 'OBJECT':
      return useFieldGroup;
    case 'COURIER_PARTNER':
      return useCourierPartner;
    case 'ENTRY_PORT':
      return useEntryPort;
    case 'SHIPPING_LINE':
      return useShippingLines;
    case 'SAILING_SCHEDULE':
      return useSailingSchedule;
    case 'RFQ_BID':
      return useRfqBids;
    case 'RFQ_QUOTE':
      return useSEQuotes;
    case 'ADDITIONAL_CHARGES':
      return useAdditionalCharges;
    case 'BL_NUMBER_LIST':
    case 'CONTAINER_NUMBER_LIST':
      return useSEGenericMultiSelect;
    case 'INCOTERM':
    case 'MODE':
    /** SMT69 basic-details dropdowns (ISS-292967) — options served by the generic dropdown endpoint, keyed by field type */
    case 'MODE_OF_SHIPMENT':
    case 'MEANS_OF_TRANSPORT':
    case 'PROCUREMENT_TYPE':
    /** SMT70 vendor-type sub-fields — options served by the generic dropdown endpoint, keyed by field type */
    case 'SEGMENT':
    case 'CARGO_TYPE':
    case 'GAUGE_STATUS':
    case 'DESTUFF_INDICATOR':
    case 'PANEL_IDENTIFIER':
    case 'CFS_BUFFER_DAYS':
    case 'ICD_BUFFER_DAYS':
    case 'MATERIAL_CATEGORY':
    case 'PLANT_TYPE':
    case 'SURVEYOR_TYPE':
    case 'SURVEYOR_CONTRACT_TYPE':
    case 'INTER_CARTING_CONTRACT_TYPE':
    case 'FF_TYPE':
      return useSEGenericSelect;
    case 'CHECKBOX':
      return useCheckbox;
    /** Container Details */
    case 'CONTAINER_SIZE':
      return useContainerSize;
    case 'CONTAINER_TYPE':
      return useContainerTypes;

    /** Packaging Details */
    case 'PACKAGE_TYPE':
      return usePackagingTypes;
    case 'DIMENSION_UNIT':
      return useValidDimensionUnits;
    case 'VOLUME_UNIT':
      return useValidVolumeUnit;
    case 'WEIGHT_UNIT':
      return useValidWeightUnit;

    case 'USER_ALLOCATION':
    case 'TEAM_AND_USER_ALLOCATION':
      return useUserAllocation;

    case 'INPUT':
    case 'TEXTAREA':
    default:
      return useInput;
  }
};

export const getGrpFieldProps = <T extends any>({
  form,
  field,
  keyCB,
  extraData,
  defaultDisabled = false,
  // duplicateField,
  // deleteField,
  dropdownMasterValues,
}: {
  form: any;
  field: Field | DocumentField;
  keyCB: (key: string) => string;
  extraData?: T;
  defaultDisabled?: boolean;
  // duplicateField?: (field: any) => void;
  // deleteField?: (field: any) => void;
  dropdownMasterValues?: DropdownMasterValues;
}) => {
  const values = isValidArray((field as Field)?.values)
    ? (field as Field)?.values.map((value) => {
        return field.type === 'DATE' ? moment(value as any) : value;
      })
    : [];
  const isObject = field.type === 'OBJECT';
  const singleComponent = isObject
    ? SEFieldGroupWithHook({
        defaultNodes:
          (field as Field).values?.map((subField) =>
            getGrpFieldProps({
              form,
              field: (subField as any) as Field,
              keyCB,
              extraData: subField,
              defaultDisabled,
            })
          ) || [],
        extraData,
        // duplicateField,
        // deleteField,
        dropdownMasterValues,
      })
    : FieldNodeSingle(
        getFieldHookByType(field.type),
        // RFQ_QUOTE fields render a single custom panel that manages its own multiplicity internally
        // (Shipping Line SMF430 splits picks per container size). Hand a multiple RFQ_QUOTE field the
        // WHOLE saved array so the panel can hydrate every per-size pick — and skip the generic
        // multi-entry wrapping below so the panel is rendered exactly once. All other multiple fields
        // keep the existing behaviour (undefined single value, wrapped by FieldNodeMulti).
        field.type === RFQ_QUOTE_FIELD_TYPE
          ? (field.isMultipleEntryType ? values : values[0])
          : (field.isMultipleEntryType ? undefined : values[0])
      );

  let renderComponent = singleComponent;

  if (
    field.isMultipleEntryType &&
    field.type !== RFQ_QUOTE_FIELD_TYPE && // RFQ_QUOTE panels own their multiplicity (see note above)
    !field.type?.includes('LIST') && // TODO: need to add a type for multiple Select
    !field.type?.includes('USER_ALLOCATION') &&
    !field.type?.includes('TEAM_AND_USER_ALLOCATION')
  ) {

    const value = isObject || !values.length ?
      [undefined]
       : values;

    renderComponent = FieldNodeMulti(singleComponent, value);
  }

  return {
    renderComponent,
    renderProps: {
      form,
      formKey: keyCB(
        ((field as Field).fieldId || (field as DocumentField).fieldTemplateId)?.toString()
      ),
      disabled: defaultDisabled || (field as Field).isReadOnly,
      visible: true,
      label: field.name,
      placeholder: `Enter ${field.name}`,
      formOptions: {
        rules: [
          {
            required: field.isMandatory,
            message: `${field.name} is required`,
          },
        ],
      },
      extraData,
    } as FieldHookProps,
  };
};

export const getGrpFieldsProps = <T extends any>({
  form,
  fields,
  keyCB,
  // extraData,
  // duplicateField,
  // deleteField,
  defaultDisabled = false,
  dropdownMasterValues,
  taskId,
}: {
  form: any;
  fields: (Field | DocumentField)[];
  keyCB: (key: string) => string;
  // extraData?: T;
  defaultDisabled?: boolean;
  // duplicateField?: (field: any) => void;
  // deleteField?: (field: any) => void;
  dropdownMasterValues?: DropdownMasterValues;
  /**
   * No field type carries a task id on its own `extraData` by convention — each backend-supplied field
   * only carries what its own feature needs (see `AdditionalChargesExtraData`). `ADDITIONAL_CHARGES`
   * (ISS-291749 / C2) is the first field that needs the task id itself, to call task-scoped Part 2
   * endpoints, so it is threaded in here rather than expected from the backend payload.
   */
  taskId?: string;
}) => {
  return (
    fields?.map((field) => {
      return getGrpFieldProps({
        form,
        field,
        keyCB,
        extraData: taskId ? { ...field, taskId } : field,
        defaultDisabled,
        // duplicateField,
        // deleteField,
        dropdownMasterValues,
      });
    }) || []
  );
};

const formatFieldValue = (val) => {
  if (val instanceof moment) return (val as any)?.valueOf();
  // if(parseFloat(val)) return parseFloat(val);
  return val;
};

export const parseFormValues = (el: any, allowMultipleValues: boolean) => {
  if (isValidArray(el)) {
    const formattedValues = el
      ?.map((val) => formatFieldValue(val))

    return formattedValues?.filter((val) => val) || [];
  }
  return el ? [formatFieldValue(el)] : [];
};
